import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@mui/material";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PermContactCalendarOutlinedIcon from "@mui/icons-material/PermContactCalendarOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import FigmaIcon from "./icons/FigmaIcon";

import rpoLogo from "../assets/icons/rpo-icon.svg";
import AddJobDialogWindow from "./PopupWindows/AddJobDialog.jsx";
import LeadDescriptionDialog from "./PopupWindows/LeadDescriptionDialog.jsx";
import AddCompanyDialog from "./PopupWindows/AddCompanyDialog.jsx";
import AddCandidateDialog from "./PopupWindows/AddCandidateDialog.jsx";
import AIAgentDialog from "./PopupWindows/AIAgentDialog.jsx";
import AddUserDialog from "./PopupWindows/AddUserDialog.jsx";
import { AddActionIcon } from "./ActionButtonIcons";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../utils/pillButtonStyles";

const maskedButtonIconSx = {
  width: 20,
  height: 20,
  display: "inline-block",
  flex: "0 0 auto",
  backgroundColor: "currentColor",
  maskRepeat: "no-repeat",
  maskPosition: "center",
  maskSize: "contain",
  WebkitMaskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  WebkitMaskSize: "contain",
};

export default function HeaderQuickActions({
  user,
  onCandidateCreated,
  variant = "desktop",
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [addAnchorEl, setAddAnchorEl] = useState(null);
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [addCandidateOpen, setAddCandidateOpen] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState(null);
  const [leadDialogMode, setLeadDialogMode] = useState("lead");
  const [leadDialogInitialScreen, setLeadDialogInitialScreen] = useState("start");
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);

  const isAdministrator = ["admin", "administrator", "system_user"].includes(
    String(user?.role || "").trim().toLowerCase()
  );

  const addMenuOpen = Boolean(addAnchorEl);

  const addMenuItems = useMemo(
    () => [
      { key: "job", label: "Job", icon: <WorkOutlineIcon fontSize="small" /> },
      {
        key: "candidate",
        label: "Candidate",
        icon: <PersonOutlineIcon fontSize="small" />,
      },
      {
        key: "company",
        label: "Company",
        icon: <ApartmentOutlinedIcon fontSize="small" />,
      },
      {
        key: "contact",
        label: "Contact",
        icon: <PermContactCalendarOutlinedIcon fontSize="small" />,
      },
      { key: "lead", label: "Lead", icon: <LeaderboardOutlinedIcon fontSize="small" /> },
      {
        key: "activity",
        label: "Activity",
        icon: <TimelineOutlinedIcon fontSize="small" />,
      },
      {
        key: "import_csv",
        label: "Import .CSV File",
        icon: <UploadFileOutlinedIcon fontSize="small" />,
      },
      ...(isAdministrator
        ? [{ key: "user", label: "User", icon: <PersonAddAlt1OutlinedIcon fontSize="small" /> }]
        : []),
    ],
    [isAdministrator]
  );

  const handleAddMenuOpen = (event) => setAddAnchorEl(event.currentTarget);
  const handleAddMenuClose = () => setAddAnchorEl(null);

  const openLeadDialog = () => {
    setLeadDialogMode("lead");
    setLeadDialogInitialScreen("start");
    setLeadForm(null);
    setAddLeadOpen(true);
  };

  const openContactDialog = () => {
    setLeadDialogMode("contact");
    setLeadDialogInitialScreen("start");
    setLeadForm(null);
    setAddLeadOpen(true);
  };

  const handleAddItemClick = (type) => {
    handleAddMenuClose();

    switch (type) {
      case "import_csv": {
        const supported = /^\/(?:candidates|companies|jobs|leads|team|content|messages-sequences)\/?$/.test(location.pathname);
        navigate(supported ? `${location.pathname}${location.search}` : "/candidates", {
          state: { ...(supported ? location.state : {}), openCsvImport: true },
        });
        break;
      }
      case "job":
        setAddJobOpen(true);
        break;
      case "candidate":
        setAddCandidateOpen(true);
        break;
      case "company":
        setAddCompanyOpen(true);
        break;
      case "contact":
        openContactDialog();
        break;
      case "lead":
        openLeadDialog();
        break;
      case "user":
        setAddUserOpen(true);
        break;
      default:
        break;
    }
  };

  const handleOpenAIAgent = () => setAiDialogOpen(true);
  const handleCloseAIAgent = () => setAiDialogOpen(false);

  const addTrigger =
    variant === "mobile" ? (
      <IconButton color="inherit" onClick={handleAddMenuOpen} aria-label="open add menu">
        <FigmaIcon name="add" color="#FFFFFF" />
      </IconButton>
    ) : (
      <Button
        variant="contained"
        className={withPillButtonClassName()}
        onClick={handleAddMenuOpen}
        sx={[
          pillButtonInteractiveSx,
          pillButtonFilledSx,
          {
            px: 2.5,
            py: 0.8,
            fontWeight: 400,
            fontSize: 14,
          },
        ]}
        startIcon={<AddActionIcon size={20} />}
      >
        Add
      </Button>
    );

  const aiTrigger =
    variant === "mobile" ? (
      <IconButton color="inherit" onClick={handleOpenAIAgent} aria-label="open ai agent">
        <FigmaIcon name="agent" color="#FFFFFF" />
      </IconButton>
    ) : (
      <Button
        variant="contained"
        className={withPillButtonClassName()}
        onClick={handleOpenAIAgent}
        sx={[
          pillButtonInteractiveSx,
          pillButtonFilledSx,
          {
            px: 2.5,
            py: 0.8,
            fontWeight: 400,
            fontSize: 14,
            boxShadow: "none",
          },
        ]}
        startIcon={
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              ...maskedButtonIconSx,
              maskImage: `url(${rpoLogo})`,
              WebkitMaskImage: `url(${rpoLogo})`,
            }}
          />
        }
      >
        AI Agent
      </Button>
    );

  return (
    <>
      {addTrigger}
      {aiTrigger}

      <Popover
        open={addMenuOpen}
        anchorEl={addAnchorEl}
        onClose={handleAddMenuClose}
        disableRestoreFocus
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            width: 260,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            p: 1,
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <List disablePadding>
          {addMenuItems.map((item, index) => (
            <React.Fragment key={item.key}>
              <ListItemButton onClick={() => handleAddItemClick(item.key)} sx={{ py: 1.2 }}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
              {index < addMenuItems.length - 1 ? <Divider /> : null}
            </React.Fragment>
          ))}
        </List>
      </Popover>

      <AddJobDialogWindow open={addJobOpen} onClose={() => setAddJobOpen(false)} />

      <AddCandidateDialog
        open={addCandidateOpen}
        onClose={() => setAddCandidateOpen(false)}
        user={user}
        showCandidateLookupField={false}
        onCreated={async (candidate) => {
          await onCandidateCreated?.(candidate);
        }}
      />

      <LeadDescriptionDialog
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        value={leadForm}
        onChange={setLeadForm}
        mode={leadDialogMode}
        initialScreen={leadDialogInitialScreen}
        readOnly={false}
        onSubmit={(form, createdContact) => {
          if (leadDialogMode !== "contact" || typeof window === "undefined") return;

          const companyId = Number(
            createdContact?.entity_id ?? createdContact?.entityId ?? form?.companyId ?? 0
          );
          if (!Number.isFinite(companyId) || companyId <= 0) return;

          window.dispatchEvent(
            new CustomEvent("company-contact-created", {
              detail: { companyId, contact: createdContact || null },
            })
          );
        }}
      />

      <AddCompanyDialog
        open={addCompanyOpen}
        onClose={() => setAddCompanyOpen(false)}
        user={user}
      />

      <AIAgentDialog open={aiDialogOpen} onClose={handleCloseAIAgent} user={user} />
      <AddUserDialog open={addUserOpen} onClose={() => setAddUserOpen(false)} user={user} />
    </>
  );
}
